import { denormalizeHighlights, normalizeHighlights } from './submission.normalizers'
import { ASSIGNMENT_PROCESSING_STATUSES } from '../../../../constants/assignment-processing-statuses'
import { RefinementItemTypes } from '../../../../pages/assignment/components/section-menu/rubric-refinement/chat/constants'

/**
 * Normalize explainability data from backend format to frontend format
 */
const normalizeExplainability = explainability => ({
    reasoning: explainability.reasoning,
    isAdequate: explainability.is_adequate,
    highlights: normalizeHighlights(explainability.highlights),
})

/**
 * Denormalize explainability data from frontend format to backend format for API calls
 */
export const denormalizeExplainability = explainability => ({
    reasoning: explainability.reasoning,
    is_adequate: explainability.isAdequate,
    highlights: denormalizeHighlights(explainability.highlights),
})

/**
 * Normalize a category from backend format to frontend format
 */
const normalizeCategory = category => ({
    id: category.id,
    description: category.description,
    order: category.order,
    tags: category.tags,
    explainability: normalizeExplainability(category.explainability),
})

/**
 * Normalize a patternIdToPatternMap from backend format to standardized frontend categories list format
 */
const normalizePatternIdToPatternMap = patternIdToPatternMap =>
    Object.values(patternIdToPatternMap)
        .map(normalizeCategory)
        .sort((a, b) => a.order - b.order)

/**
 * Normalize a submission item from backend format to the standardized frontend RefinementItem model
 */
const normalizeSubmissionItem = item => ({
    id: item.id,
    type: RefinementItemTypes.SUBMISSION,
    submission: {
        id: item.data.submission_id,
        content: item.data.submission_text,
    },
    categories: normalizePatternIdToPatternMap(item.data.pattern_id_to_pattern_map),
    groupId: item.data.group,
})

/**
 * Normalize a suggestion item from backend format to the standardized frontend RefinementItem model
 */
const normalizeRubricSuggestionItem = item => {
    // Create an array of changes from the pattern_id_to_suggestion_map
    const changes = Object.values(item.data.pattern_id_to_suggestion_map)
        .filter(pattern => pattern.description !== pattern.updated_description)
        .map(pattern => ({
            id: pattern.id,
            oldDescription: pattern.description,
            newDescription: pattern.updated_description,
            // If explainability is available, normalize it
            updatedExplainability: pattern.updated_explainability
                ? normalizeExplainability(pattern.updated_explainability)
                : null,
        }))

    // Handle grading reference if present
    const gradingReferenceChange = {
        oldGradingReference: item.data.grading_reference || null,
        newGradingReference: item.data.updated_grading_reference || null,
    }

    return {
        id: item.id,
        type: RefinementItemTypes.SUGGESTION,
        suggestions: changes,
        gradingReferenceChange,
        groupId: item.data.group,
    }
}

/**
 * Normalize progress data from backend format to frontend format
 */
const normalizeProgress = progress => ({
    itemsInQueue: progress.items_in_queue - 1, // backend returns items_in_queue + current item
    itemsReviewed: progress.items_reviewed,
})

/**
 * Normalize a reviewed item from backend format to the standardized frontend RefinementItem model
 */
const normalizeReviewedItem = item => {
    if (!item || !item.type) {
        return null
    }

    let normalizedItem

    switch (item.type) {
        case RefinementItemTypes.SUBMISSION:
            if (!item.data) {
                return null
            }

            normalizedItem = {
                id: item.id,
                type: RefinementItemTypes.SUBMISSION,
                submission: {
                    id: item.data.submission_id,
                    content: item.data.submission_text,
                },
                categories: normalizePatternIdToPatternMap(item.data.pattern_id_to_pattern_map),
                userDecision: item.user_decision,
                groupId: item.data.group,
            }
            break

        case RefinementItemTypes.SUGGESTION:
            if (!item.data) {
                return null
            }

            // Create an array of changes from the pattern_id_to_suggestion_map
            const changes = Object.values(item.data.pattern_id_to_suggestion_map || {})
                .filter(pattern => pattern.description !== pattern.updated_description)
                .map(pattern => ({
                    id: pattern.id,
                    oldDescription: pattern.description,
                    newDescription: pattern.updated_description,
                    // If explainability is available, normalize it
                    updatedExplainability: pattern.updated_explainability
                        ? normalizeExplainability(pattern.updated_explainability)
                        : null,
                }))

            // Handle grading reference if present
            const gradingReferenceChange = {
                oldGradingReference: item.data.grading_reference || null,
                newGradingReference: item.data.updated_grading_reference || null,
            }

            console.log('gradingReferenceChange', gradingReferenceChange)

            normalizedItem = {
                id: item.id,
                type: RefinementItemTypes.SUGGESTION,
                suggestions: changes,
                gradingReferenceChange,
                userDecision: item.user_decision,
                groupId: item.data.group,
            }
            break

        default:
            normalizedItem = null
    }

    return normalizedItem
}

/**
 * Normalize a refinement item response from backend format to frontend format
 */
export const normalizeRefinementItemResponse = response => {
    if (!response.item_to_review) {
        return {
            item: null,
            progress: response.progress ? normalizeProgress(response.progress) : null,
            newReviewedItemId: response.new_reviewed_item_id || null,
            gradingReference: response.grading_reference || null,
        }
    }

    const item = response.item_to_review
    let normalizedItem

    switch (item.type) {
        case RefinementItemTypes.SUBMISSION:
            normalizedItem = normalizeSubmissionItem(item)
            break
        case RefinementItemTypes.SUGGESTION:
            normalizedItem = normalizeRubricSuggestionItem(item)
            break
        default:
            console.error(`Unknown refinement item type: ${item.type}`)
            normalizedItem = null
    }

    return {
        item: normalizedItem,
        progress: normalizeProgress(response.progress),
        newReviewedItemId: response.new_reviewed_item_id || null,
        gradingReference: response.grading_reference || null,
    }
}

/**
 * Normalize a start refinement response from backend format to frontend format
 */
export const normalizeStartRefinementResponse = response => {
    // Normalize reviewed items if they exist
    const normalizedReviewedItems = response.reviewed_items?.map(normalizeReviewedItem).filter(Boolean) || []

    return {
        id: response.id,
        created: response.created,
        lastUpdate: response.last_update,
        status:
            response.processing_status === ASSIGNMENT_PROCESSING_STATUSES.REFINEMENT_INITIALIZING
                ? 'Initializing'
                : response.processing_status,
        reviewedItems: normalizedReviewedItems,
        ...normalizeRefinementItemResponse(response),
    }
}
